/**
 * Currency formatting utilities
 * Ensures consistent decimal precision (2 decimal places) across the application
 * to match database numeric(10,2) precision
 */

import apiService from '../services/api';

/** Locale for DD/MM/YYYY date format */
const DATE_LOCALE = 'en-GB';

/**
 * Removes unnecessary trailing zeros from a number string
 * 45.00 → 45, 45.10 → 45.1, 42.54 → 42.54
 */
const removeTrailingZeros = (numStr) => {
  if (!numStr.includes('.')) return numStr;
  // Remove trailing zeros after decimal point
  let result = numStr.replace(/\.?0+$/, '');
  // Handle edge case where we might have removed all decimals
  if (result.endsWith('.')) result = result.slice(0, -1);
  return result;
};

function formatLocalYmd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Normalize API/DB calendar dates to YYYY-MM-DD without UTC day-shift. */
export const toCalendarDateString = (value) => {
  if (value == null || value === '') return null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
    const d = new Date(trimmed);
    if (!Number.isNaN(d.getTime())) return formatLocalYmd(d);
    return null;
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return formatLocalYmd(value);
  }
  return null;
};

/** Today's calendar date in local timezone as YYYY-MM-DD. */
export const todayCalendarDateString = () => formatLocalYmd(new Date());

/**
 * Formats a date as DD/MM/YYYY
 * @param {string|Date} value - Date string or Date object
 * @returns {string} Formatted date (e.g. 02/03/2026)
 */
export const formatDate = (value) => {
  if (!value) return '—';
  const ymd = toCalendarDateString(value);
  if (ymd) {
    const [y, m, d] = ymd.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString(DATE_LOCALE, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }
  const d = typeof value === 'string' ? new Date(value) : value;
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(DATE_LOCALE, { day: '2-digit', month: '2-digit', year: 'numeric' });
};

/**
 * Formats a date with time as DD/MM/YYYY, HH:MM
 * @param {string|Date} value - Date string or Date object
 * @returns {string} Formatted date-time (e.g. 02/03/2026, 14:30)
 */
export const formatDateTime = (value) => {
  if (!value) return '—';
  const d = typeof value === 'string' ? new Date(value) : value;
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString(DATE_LOCALE, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Formats a number as currency with up to 2 decimal places
 * Removes trailing zeros (45.00 → 45, 45.10 → 45.1)
 * @param {number|string} value - The value to format
 * @param {boolean} includeSymbol - Whether to include "EGP" prefix
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (value, includeSymbol = false) => {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num) || num === null || num === undefined) {
    return includeSymbol ? 'EGP 0' : '0';
  }
  // Use toFixed(2) then remove trailing zeros
  const formatted = num.toFixed(2);
  // Add thousand separators
  const parts = formatted.split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const result = removeTrailingZeros(parts.join('.'));
  return includeSymbol ? `EGP ${result}` : result;
};

/**
 * Formats a currency value for display (with EGP prefix)
 * Removes trailing zeros (45.00 → 45, 45.10 → 45.1)
 * @param {number|string} value - The value to format
 * @param {string} [currencySymbol] - Optional currency symbol (e.g. 'EGP' or 'ج.م'). If omitted, uses 'EGP'
 * @returns {string} Formatted currency string with currency prefix
 */
export const formatEGP = (value, currencySymbol = 'EGP') => {
  const formatted = formatCurrency(value, false);
  return `${currencySymbol} ${formatted}`;
};

/**
 * Formats large numbers in compact form for display in small UI components
 * 1,234,567 → 1.23M, 12,345 → 12.3K, 1,234 → 1,234
 * @param {number|string} value - The value to format
 * @returns {string} Compact formatted number
 */
export const formatCompactNumber = (value) => {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num) || num === null || num === undefined) return '0';
  const abs = Math.abs(num);

  if (abs >= 1000000) {
    const compact = num / 1000000;
    return removeTrailingZeros(compact.toFixed(2)) + 'M';
  }
  if (abs >= 10000) {
    const compact = num / 1000;
    return removeTrailingZeros(compact.toFixed(1)) + 'K';
  }
  return removeTrailingZeros(num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }));
};

/**
 * Abbreviates large amounts with K / M / B (base 1000), up to `maxDecimals` fractional digits (default 3), trailing zeros stripped.
 * Values under 1000 stay as normal currency-style numbers with thousand separators (max 2 decimals).
 * @param {number|string} value
 * @param {number} [maxDecimals=3]
 * @returns {string} e.g. 34234 → "34.234K", 1_500_000 → "1.5M"
 */
export const formatAbbreviatedNumber = (value, maxDecimals = 3) => {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (num === null || num === undefined || isNaN(num)) return '0';
  if (num === 0) return '0';

  const sign = num < 0 ? '-' : '';
  const abs = Math.abs(num);

  const withSuffix = (divisor, suffix) => {
    const scaled = abs / divisor;
    const decimals = Math.min(Math.max(0, maxDecimals), 20);
    const rounded = removeTrailingZeros(scaled.toFixed(decimals));
    return `${sign}${rounded}${suffix}`;
  };

  if (abs >= 1e9) return withSuffix(1e9, 'B');
  if (abs >= 1e6) return withSuffix(1e6, 'M');
  if (abs >= 1e3) return withSuffix(1e3, 'K');

  const formatted = abs.toFixed(2);
  const trimmed = removeTrailingZeros(formatted);
  const parts = trimmed.split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return sign + parts.join('.');
};

/**
 * Formats currency in compact form for small UI components
 * EGP 1,234,567 → EGP 1.23M
 * @param {number|string} value - The value to format
 * @param {string} [currencySymbol] - Optional currency symbol. Defaults to 'EGP'
 * @returns {string} Compact formatted currency
 */
export const formatCurrencyCompact = (value, currencySymbol = 'EGP') => {
  return `${currencySymbol} ${formatCompactNumber(value)}`;
};

/**
 * Rounds a number to exactly 2 decimal places for calculations
 * This prevents floating-point precision errors
 * @param {number|string} value - The value to round
 * @returns {number} Rounded number with 2 decimal precision
 */
export const roundCurrency = (value) => {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num) || num === null || num === undefined) return 0;
  return Math.round(num * 100) / 100;
};

/**
 * Formats a quantity with up to 3 decimal places (for weight items)
 * Removes trailing zeros
 * @param {number|string} value - The quantity to format
 * @returns {string} Formatted quantity string
 */
export const formatQuantity = (value) => {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num) || num === null || num === undefined) return '0';
  // Round to 3 decimal places and remove trailing zeros
  return parseFloat(num.toFixed(3)).toString();
};

/**
 * Validates and rounds a quantity input (up to 3 decimal places)
 * @param {number|string} value - The quantity to validate
 * @returns {number} Validated quantity with max 3 decimal precision
 */
export const roundQuantity = (value) => {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num) || num === null || num === undefined) return 0;
  return Math.round(num * 1000) / 1000;
};

/**
 * Strips non-numeric characters from a money input while typing, keeping at
 * most one decimal point and `maxDecimals` fraction digits.
 */
export const normalizeMoneyInput = (value, maxDecimals = 2) => {
  let s = String(value ?? '').replace(/[^\d.]/g, '');
  let dot = s.indexOf('.');
  if (dot !== -1) {
    s = `${s.slice(0, dot + 1)}${s.slice(dot + 1).replace(/\./g, '')}`;
    dot = s.indexOf('.');
    const head = s.slice(0, dot + 1);
    const frac = s.slice(dot + 1, dot + 1 + maxDecimals);
    s = head + frac;
  }
  return s;
};

/**
 * Truncates SKU for table display; full value can be shown via title tooltip.
 * @param {string|null|undefined} sku
 */
export const formatSkuDisplay = (sku, maxLen = 17) => {
  const s = sku != null ? String(sku).trim() : '';
  if (!s) return { display: '—' };
  if (s.length <= maxLen) return { display: s };
  return { display: `${s.slice(0, maxLen)}...`, title: s };
};

/**
 * Formats markup percent with the minus sign on the left (RTL-safe): -25%, 40%
 * @param {number|null|undefined} value
 * @returns {string}
 */
export const formatMarkupPercent = (value) => {
  if (value == null || !Number.isFinite(Number(value))) return '—';
  const rounded = Math.round(Number(value));
  if (rounded < 0) return `-${Math.abs(rounded)}%`;
  return `${rounded}%`;
};

/**
 * Formats currency with the minus sign on the left (RTL-safe): -40, 25.5
 * @param {number|string} value
 * @returns {string}
 */
export const formatSignedCurrency = (value) => {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (!Number.isFinite(num)) return '0';
  const formatted = formatCurrency(Math.abs(num));
  return num < 0 ? `-${formatted}` : formatted;
};

/**
 * Uploads an image file to the tenant's uploads storage.
 * @returns {Promise<string>} Web-servable path, e.g. /uploads/<account>/offers/foo.jpg
 */
export async function saveImageToServer({ file, subdir = 'products' }) {
  if (!file) throw new Error('No image file provided');
  const dataUrl = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
  const res = await apiService.uploadImage(dataUrl, file.name, subdir);
  return res.url || res.path;
}

function getApiBaseUrlForAssets() {
  const base = apiService?.getBaseURL?.();
  if (base) return base.replace(/\/$/, '');
  const host =
    typeof window !== 'undefined' && window.location && window.location.hostname
      ? window.location.hostname
      : 'localhost';
  return `http://${host}:3001`;
}

/**
 * Converts image URLs to the correct format for display
 * Handles file:// URLs by converting to backend HTTP URLs
 * @param {string} url - The image URL to convert
 * @returns {string} Converted URL safe for display
 */
export const getImageUrl = (url) => {
  if (!url) return '';

  // Already a web URL or data URL - return as-is
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) {
    return url;
  }

  const apiBase = getApiBaseUrlForAssets();

  // Handle relative uploads paths
  if (url.startsWith('uploads/') || url.startsWith('/uploads/')) {
    const normalizedPath = url.replace(/^\//, '');
    return `${apiBase}/${normalizedPath}`;
  }

  // Convert file:// or local-file:// URLs to backend HTTP URLs
  if (url.startsWith('file://') || url.startsWith('local-file://')) {
    // Extract the path after 'uploads/'
    const uploadsIndex = url.toLowerCase().indexOf('uploads');
    if (uploadsIndex !== -1) {
      // Get everything after 'uploads' (including the /)
      let relativePath = url.substring(uploadsIndex);
      // Decode any URL encoding
      relativePath = decodeURIComponent(relativePath);
      // Normalize path separators
      relativePath = relativePath.replace(/\\/g, '/');
      return `${apiBase}/${relativePath}`;
    }
  }

  // Return as-is for other formats
  return url;
};

/**
 * Resolve logo for receipt print HTML — embed as data URL so silent print always has the image.
 * @param {string} logoPath - DB path or data URL
 * @returns {Promise<string>} src safe for <img src="...">
 */
export const resolveLogoForReceiptPrint = async (logoPath) => {
  if (!logoPath) return '';
  if (logoPath.startsWith('data:')) return logoPath;

  const url = getImageUrl(logoPath);
  if (!url) return '';

  try {
    const res = await fetch(url, { credentials: 'include' });
    if (!res.ok) return url;
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result || url);
      reader.onerror = () => reject(new Error('Failed to read logo'));
      reader.readAsDataURL(blob);
    });
  } catch {
    return url;
  }
};

/**
 * Converts an image (data URL) to 1-bit monochrome (black and white) for receipt printing.
 * Does not resize unless maxWidth is passed (store logos keep full upload resolution).
 * @param {string} dataUrl - Image as data URL (e.g. data:image/png;base64,...)
 * @param {number} threshold - Pixels above this (0-255) become white, below become black. Default 128.
 * @param {number|null|undefined} maxWidth - Optional max width; omit to keep original dimensions.
 * @returns {Promise<string>} Data URL of 1-bit monochrome PNG
 */
export const convertImageTo1BitMonochrome = (dataUrl, threshold = 128, maxWidth = null) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      try {
        let w = img.width;
        let h = img.height;
        if (maxWidth != null && Number.isFinite(maxWidth) && maxWidth > 0) {
          const cap = Math.max(40, Math.min(400, maxWidth));
          if (w > cap) {
            h = Math.max(1, Math.round((h * cap) / w));
            w = cap;
          }
        }
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, w, h);
        ctx.drawImage(img, 0, 0, w, h);
        const imageData = ctx.getImageData(0, 0, w, h);
        const data = imageData.data;
        for (let i = 0; i < data.length; i += 4) {
          const gray = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
          const value = gray > threshold ? 255 : 0;
          data[i] = data[i + 1] = data[i + 2] = value;
        }
        ctx.putImageData(imageData, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      } catch (err) {
        reject(err);
      }
    };
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = dataUrl;
  });
};

const MAX_INLINE_LOGO_CHARS = 120000;

/**
 * Before saving store settings: monochrome at full resolution, then save as file (not inline base64).
 * Receipt print width is controlled separately by logo_size in settings.
 * @returns {Promise<string|null>}
 */
export const persistStoreLogoPath = async (logoPath) => {
  if (!logoPath) return null;
  let path = String(logoPath);
  if (!path.startsWith('data:')) return path;

  try {
    path = await convertImageTo1BitMonochrome(path, 128);
  } catch (err) {
    console.error('Logo prepare failed:', err);
  }

  if (path.startsWith('data:')) {
    try {
      const res = await apiService.uploadImage(path, 'logo.png', 'store');
      return res.path || res.url || path;
    } catch (err) {
      console.error('Failed to upload logo:', err);
    }
  }

  if (path.length > MAX_INLINE_LOGO_CHARS) {
    throw new Error(
      'Logo file is too large to store in settings. Use the Numuw Counter desktop app so the logo is saved as a file.',
    );
  }
  return path;
};

/**
 * Normalize image path for DB storage (relative uploads/ path when possible).
 * @param {string|null|undefined} value
 * @returns {string|null}
 */
export function normalizeImagePathForStorage(value) {
  if (value === undefined || value === null) return null;
  const raw = String(value).trim();
  if (!raw) return null;

  if (raw.startsWith('data:')) return raw;
  if (raw.startsWith('https://')) return raw;

  let path = raw;

  if (path.startsWith('http://') || path.startsWith('https://')) {
    try {
      const u = new URL(path);
      path = u.pathname.replace(/^\/+/, '');
    } catch {
      return raw;
    }
  }

  path = path.replace(/^\/+/, '').replace(/\\/g, '/');

  if (path.startsWith('file://') || path.startsWith('local-file://')) {
    const idx = path.toLowerCase().indexOf('uploads');
    if (idx !== -1) path = path.substring(idx);
  }

  if (path.includes('..')) {
    throw new Error('Invalid image path');
  }

  if (path.startsWith('uploads/')) return path;

  return raw;
}
